import { Text } from "@hackthenorth/north";
import React from "react";
import styled from "styled-components";

import {
  ChatIcon,
  ChipIcon,
  GearIcon,
  NetworkIcon,
  QRCodeIcon,
} from "src/static/icons";
import {
  Quest,
  QuestBackgroundColorMap,
  QuestPrimaryColorMap,
  QuestType,
} from "src/views/goose-games/types";

import { BalanceChip } from "../../balanceChip";
import { QUESTS } from "../../modals/hacker/home/quest/constants";

const CatchUpQuestCard = ({
  quest,
  progress,
  pointsIncreased = 0,
}: {
  quest: Quest;
  progress?: number;
  pointsIncreased?: number;
}) => {
  const completedQuest =
    progress !== undefined && progress >= quest.completion_limit;

  return (
    <>
      <Card>
        <FlexHeading>
          <TypeContainer>
            <IconOutline type={quest.type}>
              {quest.type === QuestType.Scanned ? (
                <QRCodeIcon width={24} color={QUESTS.SCANNED.color} />
              ) : quest.type === QuestType.OrganizerApproved ? (
                <GearIcon width={24} />
              ) : quest.type === QuestType.Question ? (
                <ChatIcon width={24} />
              ) : quest.type === QuestType.Badge ? (
                <ChipIcon width={24} />
              ) : (
                <NetworkIcon width={24} />
              )}
            </IconOutline>
            <TitleText mods="heading h3">{quest.title}</TitleText>
            {quest.completion_limit > 1 && (
              <IncreasedText mods="heading h3">{`(+${pointsIncreased})`}</IncreasedText>
            )}
          </TypeContainer>
          <TypeContainer>
            {quest.completion_limit <= 1 && completedQuest ? (
              <StatusOutline isCompleted={completedQuest}>
                <StatusText mods="p" isCompleted={completedQuest}>
                  Complete
                </StatusText>
              </StatusOutline>
            ) : (
              <StatusOutline isCompleted={completedQuest}>
                <StatusText mods="p" isCompleted={completedQuest}>
                  {progress === undefined ? 0 : progress}/
                  {quest.completion_limit}
                </StatusText>
              </StatusOutline>
            )}
            <BalanceChip
              isComplete={false}
              points={quest.points_rewarded}
              completionLimit={0}
            />
          </TypeContainer>
        </FlexHeading>
      </Card>
    </>
  );
};

const FlexHeading = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
`;

const IconOutline = styled.div<{ type: QuestType }>`
  background: ${({ type }) => QuestBackgroundColorMap[type]};
  color: ${({ type }) => QuestPrimaryColorMap[type]};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px;
  border-radius: 32px;
  width: 32px;
  height: 32px;
`;

const TypeContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const StatusOutline = styled.div<{ isCompleted: boolean }>`
  background: ${({ theme, isCompleted }) =>
    isCompleted
      ? theme.globalConstants.color.backgroundSuccessLight
      : theme.globalConstants.color.backgroundGray};
  display: flex;
  padding: 4px 9.189px;
  align-items: center;
  gap: 4px;
  border-radius: 32px;
  height: 32px;
`;

const StatusText = styled(Text)<{ isCompleted: boolean }>`
  color: ${({ theme, isCompleted }) =>
    isCompleted
      ? theme.globalConstants.color.textSuccess
      : "#6B7280"} !important;
  font-size: 14px;
  font-weight: 700;
`;

const TitleText = styled(Text)`
  color: #374151 !important;
  line-height: normal !important;
  margin: 0 !important;
`;

const IncreasedText = styled(Text)`
  color: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimary} !important;
  line-height: normal !important;
  margin: 0 !important;
`;

const Card = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  border-radius: 8px;
  border: 1px solid var(--North-Border-Gray, #d1d5db);
  background: ${({ theme }) => theme.globalConstants.color.white};
  transition: background-color 0.2s ease;
`;

export default CatchUpQuestCard;
